import { useState, useEffect, useCallback } from 'react';
import { apiClient, useAuth } from '../contexts/AuthContext';
import { BottomNav } from '../components/BottomNav';
import { MediaUpload, MediaPreview } from '../components/MediaUpload';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { MessageSquare, Salad, Heart, Plus, Trash2, Flame, Image } from 'lucide-react';
import { toast } from 'sonner';

export default function CommunautePage({ embedded = false }) {
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [postType, setPostType] = useState('tip');
  const [tipContent, setTipContent] = useState('');
  const [tipMedia, setTipMedia] = useState([]);
  const [dietForm, setDietForm] = useState({ content: '', diet_objective: '', diet_calories: '', diet_protein: '', diet_carbs: '', diet_fat: '', diet_meals: '' });
  const [dietMedia, setDietMedia] = useState([]);

  const fetchPosts = useCallback(async () => {
    try {
      const { data } = await apiClient.get('/community');
      setPosts(data);
    } catch { toast.error('Erreur chargement'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchPosts(); }, [fetchPosts]);

  const handleCreateTip = async () => {
    if (!tipContent.trim()) return;
    try {
      await apiClient.post('/community', { type: 'tip', content: tipContent.trim(), media_urls: tipMedia });
      toast.success('Bon plan partagé !');
      setTipContent(''); setTipMedia([]); setShowCreate(false);
      fetchPosts();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const handleCreateDiet = async () => {
    if (!dietForm.content.trim()) return;
    try {
      await apiClient.post('/community', {
        type: 'diet', content: dietForm.content, media_urls: dietMedia,
        diet_objective: dietForm.diet_objective,
        diet_calories: parseInt(dietForm.diet_calories) || null,
        diet_protein: parseFloat(dietForm.diet_protein) || null,
        diet_carbs: parseFloat(dietForm.diet_carbs) || null,
        diet_fat: parseFloat(dietForm.diet_fat) || null,
        diet_meals: dietForm.diet_meals
      });
      toast.success('Programme diet partagé !');
      setDietForm({ content: '', diet_objective: '', diet_calories: '', diet_protein: '', diet_carbs: '', diet_fat: '', diet_meals: '' });
      setDietMedia([]); setShowCreate(false);
      fetchPosts();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const handleLike = async (id) => {
    try { await apiClient.post(`/community/${id}/like`); fetchPosts(); }
    catch { toast.error('Erreur'); }
  };

  const handleDelete = async (id) => {
    try { await apiClient.delete(`/community/${id}`); toast.success('Supprimé'); fetchPosts(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const timeAgo = (dateStr) => {
    const diff = (Date.now() - new Date(dateStr)) / 1000;
    if (diff < 60) return 'maintenant';
    if (diff < 3600) return `${Math.floor(diff / 60)}min`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
    return `${Math.floor(diff / 86400)}j`;
  };

  if (loading) return (
    <div className="min-h-screen bg-[#0F0F0F] flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-[#FF6B35] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <div className={`${embedded ? "" : "min-h-screen"} bg-[#0F0F0F] ${embedded ? "pb-4" : "pb-20"}`}>
      {!embedded && <header className="sticky top-0 z-40 bg-[#0F0F0F]/95 backdrop-blur-sm border-b border-[#2A2A2E]">
        <div className="flex items-center justify-between px-4 py-3">
          <div>
            <h1 className="text-xl font-bold text-white">Communauté</h1>
            <p className="text-xs text-zinc-500">Bons plans & programmes diet</p>
          </div>
          <Button onClick={() => setShowCreate(true)} className="bg-[#FF6B35] text-black hover:bg-[#E85A25]" size="sm" data-testid="create-post-btn">
            <Plus className="w-4 h-4 mr-1" /> Publier
          </Button>
        </div>
      </header>}
      {embedded && (
        <div className="flex justify-end px-4 pt-3">
          <Button onClick={() => setShowCreate(true)} className="bg-[#FF6B35] text-black hover:bg-[#E85A25]" size="sm" data-testid="create-post-btn">
            <Plus className="w-4 h-4 mr-1" /> Publier
          </Button>
        </div>
      )}

      <main className="p-4 space-y-3 animate-fade-in">
        {posts.length > 0 ? posts.map((post) => {
          const isMine = post.user_id === user?.id;
          const liked = (post.likes || []).includes(user?.id);
          const likesCount = (post.likes || []).length;
          const mediaUrls = post.media_urls || (post.image_url ? [post.image_url] : []);

          return (
            <div key={post.id} className="bg-[#1B1B1B] border border-[#2A2A2E] rounded-sm p-4" data-testid={`post-${post.id}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-full bg-zinc-800 flex items-center justify-center text-xs font-bold text-zinc-400">
                    {post.user_name?.charAt(0)?.toUpperCase()}
                  </div>
                  <span className="text-sm text-white font-medium">{post.user_name}</span>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-sm ${post.type === 'tip' ? 'bg-[#FF6B35]/20 text-[#FF6B35]' : 'bg-green-500/20 text-green-400'}`}>
                    {post.type === 'tip' ? 'Bon plan' : 'Diet'}
                  </span>
                  <span className="text-xs text-zinc-400">{timeAgo(post.created_at)}</span>
                </div>
                {(isMine || user?.role === 'admin') && (
                  <button onClick={() => handleDelete(post.id)} className="text-zinc-500 hover:text-red-400">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>

              <p className="text-sm text-zinc-300 mb-2">{post.content}</p>

              {/* Media */}
              {mediaUrls.length > 0 && (
                <div className="flex gap-2 flex-wrap mb-2">
                  {mediaUrls.map((url, i) => {
                    const isImg = url.match(/\.(jpg|jpeg|png|gif|webp)/i) || url.includes('/api/files/');
                    return isImg ? (
                      <img key={`${url}-${i}`} src={url} alt="" className="w-full max-w-[200px] rounded-sm object-cover border border-[#2A2A2E]" />
                    ) : (
                      <a key={`${url}-${i}`} href={url} target="_blank" rel="noreferrer" className="text-xs text-blue-400 underline break-all">{url}</a>
                    );
                  })}
                </div>
              )}

              {/* Diet details */}
              {post.type === 'diet' && (
                <div className="bg-[#0F0F0F] border border-[#2A2A2E] rounded-sm p-3 mb-2 space-y-2">
                  {post.diet_objective && <p className="text-xs text-[#FF6B35] font-medium">Objectif: {post.diet_objective}</p>}
                  {(post.diet_calories || post.diet_protein) && (
                    <div className="flex gap-3 text-xs text-zinc-400">
                      {post.diet_calories && <span>{post.diet_calories} kcal</span>}
                      {post.diet_protein && <span>P:{post.diet_protein}g</span>}
                      {post.diet_carbs && <span>G:{post.diet_carbs}g</span>}
                      {post.diet_fat && <span>L:{post.diet_fat}g</span>}
                    </div>
                  )}
                  {post.diet_meals && <p className="text-xs text-zinc-500 whitespace-pre-line">{post.diet_meals}</p>}
                </div>
              )}

              <button onClick={() => handleLike(post.id)} className={`flex items-center gap-1 text-xs ${liked ? 'text-red-400' : 'text-zinc-500 hover:text-red-400'}`}>
                <Heart className={`w-3.5 h-3.5 ${liked ? 'fill-red-400' : ''}`} /> {likesCount > 0 && likesCount}
              </button>
            </div>
          );
        }) : (
          <div className="bg-[#1B1B1B] border border-[#2A2A2E] rounded-sm p-8 text-center">
            <MessageSquare className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
            <p className="text-zinc-400">Aucun post pour le moment</p>
          </div>
        )}
      </main>

      {/* Create Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="bg-[#1B1B1B] border-[#2A2A2E] text-white max-w-md max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-white">Nouvelle publication</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="flex gap-2">
              <button onClick={() => setPostType('tip')} className={`flex-1 py-2 rounded-sm text-sm font-medium ${postType === 'tip' ? 'bg-[#FF6B35] text-black' : 'bg-zinc-800 text-zinc-400'}`}>
                <Flame className="w-4 h-4 inline mr-1" /> Bon plan
              </button>
              <button onClick={() => setPostType('diet')} className={`flex-1 py-2 rounded-sm text-sm font-medium ${postType === 'diet' ? 'bg-green-600 text-white' : 'bg-zinc-800 text-zinc-400'}`}>
                <Salad className="w-4 h-4 inline mr-1" /> Programme diet
              </button>
            </div>

            {postType === 'tip' ? (
              <div className="space-y-2">
                <Label className="text-zinc-400 text-xs">Ton conseil (150 car. max)</Label>
                <Textarea value={tipContent} onChange={e => setTipContent(e.target.value)} maxLength={150} placeholder="Ex: Boire 500ml d'eau avant chaque séance..." className="bg-[#0F0F0F] border-[#2A2A2E] text-white" />
                <p className="text-xs text-zinc-400 text-right">{tipContent.length}/150</p>
                <MediaUpload onMediaAdded={url => setTipMedia([...tipMedia, url])} />
                <MediaPreview urls={tipMedia} onRemove={i => setTipMedia(tipMedia.filter((_, idx) => idx !== i))} />
                <Button onClick={handleCreateTip} className="w-full bg-[#FF6B35] text-black hover:bg-[#E85A25]" data-testid="submit-tip">Publier</Button>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="space-y-2">
                  <Label className="text-zinc-400 text-xs">Description du programme</Label>
                  <Textarea value={dietForm.content} onChange={e => setDietForm({...dietForm, content: e.target.value})} placeholder="Mon plan nutrition pour la prise de masse..." className="bg-[#0F0F0F] border-[#2A2A2E] text-white min-h-[50px]" />
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-400 text-xs">Objectif</Label>
                  <Input value={dietForm.diet_objective} onChange={e => setDietForm({...dietForm, diet_objective: e.target.value})} placeholder="Prise de masse / Sèche / Maintien" className="bg-[#0F0F0F] border-[#2A2A2E] text-white" />
                </div>
                <div className="grid grid-cols-4 gap-2">
                  <div><Label className="text-zinc-500 text-[10px]">Calories</Label><Input type="number" value={dietForm.diet_calories} onChange={e => setDietForm({...dietForm, diet_calories: e.target.value})} className="bg-[#0F0F0F] border-[#2A2A2E] text-white font-mono text-sm" /></div>
                  <div><Label className="text-zinc-500 text-[10px]">Prot (g)</Label><Input type="number" value={dietForm.diet_protein} onChange={e => setDietForm({...dietForm, diet_protein: e.target.value})} className="bg-[#0F0F0F] border-[#2A2A2E] text-white font-mono text-sm" /></div>
                  <div><Label className="text-zinc-500 text-[10px]">Gluc (g)</Label><Input type="number" value={dietForm.diet_carbs} onChange={e => setDietForm({...dietForm, diet_carbs: e.target.value})} className="bg-[#0F0F0F] border-[#2A2A2E] text-white font-mono text-sm" /></div>
                  <div><Label className="text-zinc-500 text-[10px]">Lipides (g)</Label><Input type="number" value={dietForm.diet_fat} onChange={e => setDietForm({...dietForm, diet_fat: e.target.value})} className="bg-[#0F0F0F] border-[#2A2A2E] text-white font-mono text-sm" /></div>
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-400 text-xs">Repas type (détail)</Label>
                  <Textarea value={dietForm.diet_meals} onChange={e => setDietForm({...dietForm, diet_meals: e.target.value})} placeholder="Petit-déj: 4 oeufs + avoine&#10;Midi: Poulet 200g + riz&#10;Snack: Shake protéine..." className="bg-[#0F0F0F] border-[#2A2A2E] text-white min-h-[80px]" />
                </div>
                <MediaUpload onMediaAdded={url => setDietMedia([...dietMedia, url])} />
                <MediaPreview urls={dietMedia} onRemove={i => setDietMedia(dietMedia.filter((_, idx) => idx !== i))} />
                <Button onClick={handleCreateDiet} className="w-full bg-green-600 text-white hover:bg-green-700" data-testid="submit-diet">Publier le programme</Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {!embedded && <BottomNav />}
    </div>
  );
}
